import React from "react";

/** Animated SVG mesh grid — used as section background */
export const SvgMeshGrid: React.FC<{ opacity?: number }> = ({ opacity = 0.06 }) => (
  <svg
    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", opacity }}
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="xMidYMid slice"
  >
    <defs>
      <pattern id="mesh" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
        <path d="M60 0L0 0 0 60" fill="none" stroke="rgba(140,255,46,0.5)" strokeWidth="0.5"/>
      </pattern>
      <radialGradient id="meshFade" cx="50%" cy="50%" r="55%">
        <stop offset="0%" stopColor="white" stopOpacity="1"/>
        <stop offset="100%" stopColor="white" stopOpacity="0"/>
      </radialGradient>
      <mask id="meshMask">
        <rect width="100%" height="100%" fill="url(#meshFade)"/>
      </mask>
    </defs>
    <rect width="100%" height="100%" fill="url(#mesh)" mask="url(#meshMask)"/>
  </svg>
);

/** Glowing horizontal section divider */
export const SvgSectionDivider: React.FC<{ flip?: boolean }> = ({ flip }) => (
  <svg
    viewBox="0 0 1440 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block", width: "100%", transform: flip ? "scaleY(-1)" : undefined }}
    preserveAspectRatio="none"
  >
    <defs>
      <linearGradient id={`divGrad${flip}`} x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%"   stopColor="#000"/>
        <stop offset="20%"  stopColor="#060d08"/>
        <stop offset="50%"  stopColor="#0a1a0c"/>
        <stop offset="80%"  stopColor="#060d08"/>
        <stop offset="100%" stopColor="#000"/>
      </linearGradient>
      <linearGradient id="lineGlow" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%"   stopColor="transparent"/>
        <stop offset="30%"  stopColor="rgba(140,255,46,0.4)"/>
        <stop offset="50%"  stopColor="rgba(140,255,46,0.8)"/>
        <stop offset="70%"  stopColor="rgba(140,255,46,0.4)"/>
        <stop offset="100%" stopColor="transparent"/>
      </linearGradient>
    </defs>
    <path d="M0 80C240 80 480 20 720 20C960 20 1200 80 1440 80V80H0Z" fill={`url(#divGrad${flip})`}/>
    <path d="M0 80C240 80 480 20 720 20C960 20 1200 80 1440 80" fill="none" stroke="url(#lineGlow)" strokeWidth="1"/>
    {/* glow dots on the curve */}
    <circle cx="720" cy="20" r="3" fill="#8cff2e" opacity="0.9">
      <animate attributeName="opacity" values="0.4;1;0.4" dur="3s" repeatCount="indefinite"/>
    </circle>
    <circle cx="360" cy="50" r="2" fill="#8cff2e" opacity="0.5">
      <animate attributeName="opacity" values="0.2;0.7;0.2" dur="4s" repeatCount="indefinite"/>
    </circle>
    <circle cx="1080" cy="50" r="2" fill="#8cff2e" opacity="0.5">
      <animate attributeName="opacity" values="0.2;0.7;0.2" dur="3.5s" repeatCount="indefinite"/>
    </circle>
  </svg>
);

/** Corner ornament — top-left or top-right */
export const SvgCornerOrn: React.FC<{ position?: "tl"|"tr"|"bl"|"br" }> = ({ position = "tl" }) => {
  const style: React.CSSProperties = {
    position: "absolute",
    width: 120, height: 120,
    pointerEvents: "none",
    opacity: 0.35,
    ...(position === "tl" ? { top: 0, left: 0 } : {}),
    ...(position === "tr" ? { top: 0, right: 0, transform: "scaleX(-1)" } : {}),
    ...(position === "bl" ? { bottom: 0, left: 0, transform: "scaleY(-1)" } : {}),
    ...(position === "br" ? { bottom: 0, right: 0, transform: "scale(-1)" } : {}),
  };
  return (
    <svg style={style} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 0L40 0" stroke="rgba(140,255,46,0.6)" strokeWidth="1"/>
      <path d="M0 0L0 40" stroke="rgba(140,255,46,0.6)" strokeWidth="1"/>
      <path d="M0 0L20 0L20 20L0 20Z" fill="none" stroke="rgba(140,255,46,0.2)" strokeWidth="0.5"/>
      <circle cx="0" cy="0" r="3" fill="#8cff2e" opacity="0.8"/>
      <circle cx="40" cy="0" r="1.5" fill="#8cff2e" opacity="0.4"/>
      <circle cx="0" cy="40" r="1.5" fill="#8cff2e" opacity="0.4"/>
    </svg>
  );
};

/** Floating hex grid — decorative background element */
export const SvgHexGrid: React.FC = () => (
  <svg
    style={{ position: "absolute", right: 0, top: "10%", width: 320, height: 400, pointerEvents: "none", opacity: 0.04 }}
    viewBox="0 0 320 400" fill="none" xmlns="http://www.w3.org/2000/svg"
  >
    {Array.from({ length: 5 }, (_, row) =>
      Array.from({ length: 4 }, (_, col) => {
        const x = col * 70 + (row % 2 === 0 ? 0 : 35);
        const y = row * 60;
        const pts = [
          [x+30, y], [x+60, y+15], [x+60, y+45],
          [x+30, y+60], [x, y+45], [x, y+15],
        ].map(p => p.join(",")).join(" ");
        return <polygon key={`${row}-${col}`} points={pts} stroke="#8cff2e" strokeWidth="1" fill="none"/>;
      })
    )}
  </svg>
);

/** Animated scan line — used in hero */
export const SvgScanLine: React.FC = () => (
  <svg
    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", opacity: 0.06 }}
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="scanGrad" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="transparent"/>
        <stop offset="48%" stopColor="transparent"/>
        <stop offset="50%" stopColor="rgba(140,255,46,0.8)"/>
        <stop offset="52%" stopColor="transparent"/>
        <stop offset="100%" stopColor="transparent"/>
      </linearGradient>
    </defs>
    <rect width="100%" height="200%" fill="url(#scanGrad)">
      <animateTransform attributeName="transform" type="translate" from="0 -100%" to="0 100%" dur="6s" begin="2s" repeatCount="indefinite"/>
    </rect>
  </svg>
);

/** Glowing ring SVG — replaces CSS orbit rings with richer SVG version */
export const SvgOrbitRings: React.FC = () => (
  <svg
    style={{ position: "absolute", top: "50%", left: "60%", transform: "translate(-50%,-50%)",
      width: 560, height: 560, pointerEvents: "none", overflow: "visible" }}
    viewBox="-280 -280 560 560" fill="none" xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <radialGradient id="ringFade" cx="50%" cy="50%" r="50%">
        <stop offset="60%" stopColor="white" stopOpacity="1"/>
        <stop offset="100%" stopColor="white" stopOpacity="0"/>
      </radialGradient>
      <mask id="ringMask">
        <rect x="-280" y="-280" width="560" height="560" fill="url(#ringFade)"/>
      </mask>
    </defs>
    {/* ring 1 */}
    <circle cx="0" cy="0" r="260" stroke="rgba(140,255,46,0.15)" strokeWidth="1" mask="url(#ringMask)">
      <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="28s" repeatCount="indefinite"/>
    </circle>
    {/* ring 2 */}
    <circle cx="0" cy="0" r="190" stroke="rgba(140,255,46,0.1)" strokeWidth="1" strokeDasharray="8 12" mask="url(#ringMask)">
      <animateTransform attributeName="transform" type="rotate" from="360" to="0" dur="20s" repeatCount="indefinite"/>
    </circle>
    {/* ring 3 */}
    <circle cx="0" cy="0" r="120" stroke="rgba(140,255,46,0.18)" strokeWidth="1" mask="url(#ringMask)">
      <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="14s" repeatCount="indefinite"/>
    </circle>
    {/* orbiting dots */}
    <g>
      <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="10s" repeatCount="indefinite"/>
      <circle cx="260" cy="0" r="4" fill="#8cff2e" opacity="0.9">
        <animate attributeName="opacity" values="0.4;1;0.4" dur="2s" repeatCount="indefinite"/>
      </circle>
    </g>
    <g>
      <animateTransform attributeName="transform" type="rotate" from="120" to="480" dur="7s" repeatCount="indefinite"/>
      <circle cx="190" cy="0" r="3" fill="#8cff2e" opacity="0.7"/>
    </g>
    <g>
      <animateTransform attributeName="transform" type="rotate" from="240" to="600" dur="14s" repeatCount="indefinite"/>
      <circle cx="120" cy="0" r="2.5" fill="#8cff2e" opacity="0.6"/>
    </g>
    <g>
      <animateTransform attributeName="transform" type="rotate" from="60" to="420" dur="11s" repeatCount="indefinite"/>
      <circle cx="260" cy="0" r="2" fill="rgba(255,255,255,0.6)" opacity="0.5"/>
    </g>
  </svg>
);

/** Footer cityscape SVG */
export const SvgCityscape: React.FC = () => (
  <svg
    viewBox="0 0 1440 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block", width: "100%", opacity: 0.07 }}
    preserveAspectRatio="xMidYMax meet"
  >
    {/* buildings */}
    <rect x="0"    y="60"  width="60"  height="60" fill="#8cff2e"/>
    <rect x="10"   y="40"  width="20"  height="20" fill="#8cff2e"/>
    <rect x="70"   y="30"  width="80"  height="90" fill="#8cff2e"/>
    <rect x="90"   y="10"  width="20"  height="20" fill="#8cff2e"/>
    <rect x="160"  y="50"  width="50"  height="70" fill="#8cff2e"/>
    <rect x="220"  y="20"  width="70"  height="100" fill="#8cff2e"/>
    <rect x="235"  y="5"   width="15"  height="15" fill="#8cff2e"/>
    <rect x="300"  y="55"  width="40"  height="65" fill="#8cff2e"/>
    <rect x="350"  y="35"  width="60"  height="85" fill="#8cff2e"/>
    <rect x="420"  y="60"  width="45"  height="60" fill="#8cff2e"/>
    <rect x="475"  y="25"  width="55"  height="95" fill="#8cff2e"/>
    <rect x="490"  y="10"  width="12"  height="15" fill="#8cff2e"/>
    <rect x="540"  y="50"  width="40"  height="70" fill="#8cff2e"/>
    <rect x="590"  y="40"  width="65"  height="80" fill="#8cff2e"/>
    <rect x="665"  y="15"  width="50"  height="105" fill="#8cff2e"/>
    <rect x="680"  y="0"   width="10"  height="15" fill="#8cff2e"/>
    <rect x="725"  y="55"  width="45"  height="65" fill="#8cff2e"/>
    <rect x="780"  y="30"  width="70"  height="90" fill="#8cff2e"/>
    <rect x="860"  y="50"  width="50"  height="70" fill="#8cff2e"/>
    <rect x="920"  y="20"  width="60"  height="100" fill="#8cff2e"/>
    <rect x="990"  y="45"  width="45"  height="75" fill="#8cff2e"/>
    <rect x="1045" y="35"  width="55"  height="85" fill="#8cff2e"/>
    <rect x="1110" y="55"  width="40"  height="65" fill="#8cff2e"/>
    <rect x="1160" y="25"  width="65"  height="95" fill="#8cff2e"/>
    <rect x="1235" y="50"  width="50"  height="70" fill="#8cff2e"/>
    <rect x="1295" y="40"  width="60"  height="80" fill="#8cff2e"/>
    <rect x="1365" y="60"  width="75"  height="60" fill="#8cff2e"/>
    {/* windows */}
    {[80,100,120,140,230,250,270,480,500,670,690,785,800,925,940,1050,1070,1165,1185].map((x, i) => (
      <rect key={i} x={x} y={45 + (i % 3) * 15} width="6" height="6" fill="rgba(0,0,0,0.4)"/>
    ))}
    {/* metro line */}
    <rect x="0" y="115" width="1440" height="5" fill="#8cff2e" opacity="0.5"/>
    <rect x="0" y="112" width="1440" height="1" fill="#8cff2e" opacity="0.3"/>
  </svg>
);

/** Decorative SVG lines for blog cards */
export const SvgCardLines: React.FC<{ color: string }> = ({ color }) => (
  <svg
    style={{ position: "absolute", top: 0, right: 0, width: 160, height: 160, pointerEvents: "none", opacity: 0.12 }}
    viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="160" cy="0" r="80"  stroke={color} strokeWidth="0.8"/>
    <circle cx="160" cy="0" r="120" stroke={color} strokeWidth="0.5"/>
    <circle cx="160" cy="0" r="50"  stroke={color} strokeWidth="1"/>
    <circle cx="160" cy="0" r="6"   fill={color} opacity="0.6"/>
  </svg>
);

/** Animated dot-grid — hero background accent */
export const SvgDotGrid: React.FC<{ opacity?: number }> = ({ opacity = 0.18 }) => (
  <svg
    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", opacity }}
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="xMidYMid slice"
  >
    <defs>
      <pattern id="dotgrid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
        <circle cx="1" cy="1" r="1" fill="rgba(140,255,46,0.55)" />
      </pattern>
      <radialGradient id="dotFade" cx="50%" cy="40%" r="55%">
        <stop offset="0%"   stopColor="white" stopOpacity="1"/>
        <stop offset="100%" stopColor="white" stopOpacity="0"/>
      </radialGradient>
      <mask id="dotMask">
        <rect width="100%" height="100%" fill="url(#dotFade)"/>
      </mask>
    </defs>
    <rect width="100%" height="100%" fill="url(#dotgrid)" mask="url(#dotMask)"/>
  </svg>
);

/** Glowing diagonal lines — section accent */
export const SvgDiagLines: React.FC<{ opacity?: number }> = ({ opacity = 0.06 }) => (
  <svg
    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", opacity }}
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="xMidYMid slice"
  >
    <defs>
      <pattern id="diaglines" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
        <line x1="0" y1="60" x2="60" y2="0" stroke="rgba(140,255,46,0.6)" strokeWidth="0.6"/>
      </pattern>
      <radialGradient id="diagFade" cx="50%" cy="50%" r="50%">
        <stop offset="0%"   stopColor="white" stopOpacity="1"/>
        <stop offset="100%" stopColor="white" stopOpacity="0"/>
      </radialGradient>
      <mask id="diagMask">
        <rect width="100%" height="100%" fill="url(#diagFade)"/>
      </mask>
    </defs>
    <rect width="100%" height="100%" fill="url(#diaglines)" mask="url(#diagMask)"/>
  </svg>
);

/** Radial glow burst — hero center accent */
export const SvgGlowBurst: React.FC<{ color?: string; opacity?: number }> = ({
  color = "rgba(140,255,46,0.18)",
  opacity = 1,
}) => (
  <svg
    style={{ position: "absolute", top: "50%", left: "55%", transform: "translate(-50%,-50%)",
      width: 900, height: 900, pointerEvents: "none", opacity, overflow: "visible" }}
    viewBox="-450 -450 900 900" fill="none" xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <radialGradient id="burst" cx="50%" cy="50%" r="50%">
        <stop offset="0%"   stopColor={color} stopOpacity="1"/>
        <stop offset="100%" stopColor={color} stopOpacity="0"/>
      </radialGradient>
    </defs>
    <circle cx="0" cy="0" r="420" fill="url(#burst)" />
    {/* subtle spokes */}
    {Array.from({ length: 12 }, (_, i) => {
      const angle = (i * 30 * Math.PI) / 180;
      return (
        <line key={i}
          x1="0" y1="0"
          x2={Math.cos(angle) * 420} y2={Math.sin(angle) * 420}
          stroke="rgba(140,255,46,0.06)" strokeWidth="1"
        />
      );
    })}
  </svg>
);

/** Floating particles — scattered glowing dots */
export const SvgParticles: React.FC = () => (
  <svg
    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", overflow: "hidden" }}
    xmlns="http://www.w3.org/2000/svg"
  >
    {[
      { cx: "12%",  cy: "18%", r: 1.5, dur: "4s",  delay: "0s"   },
      { cx: "88%",  cy: "12%", r: 2,   dur: "5s",  delay: "1s"   },
      { cx: "75%",  cy: "72%", r: 1.5, dur: "6s",  delay: "0.5s" },
      { cx: "22%",  cy: "65%", r: 1,   dur: "7s",  delay: "2s"   },
      { cx: "55%",  cy: "88%", r: 2,   dur: "4.5s",delay: "1.5s" },
      { cx: "92%",  cy: "45%", r: 1.5, dur: "5.5s",delay: "0.8s" },
      { cx: "8%",   cy: "50%", r: 1,   dur: "6.5s",delay: "3s"   },
      { cx: "40%",  cy: "8%",  r: 2,   dur: "5s",  delay: "2.5s" },
      { cx: "65%",  cy: "30%", r: 1.5, dur: "4s",  delay: "1.2s" },
      { cx: "30%",  cy: "40%", r: 1,   dur: "7s",  delay: "0.3s" },
    ].map((p, i) => (
      <circle key={i} cx={p.cx} cy={p.cy} r={p.r} fill="#8cff2e" opacity="0">
        <animate attributeName="opacity" values="0;0.7;0" dur={p.dur}
          begin={p.delay} repeatCount="indefinite"/>
        <animate attributeName="r" values={`${p.r};${p.r * 1.8};${p.r}`} dur={p.dur}
          begin={p.delay} repeatCount="indefinite"/>
      </circle>
    ))}
  </svg>
);

/** Animated circuit-board lines — What section accent */
export const SvgCircuit: React.FC<{ opacity?: number }> = ({ opacity = 0.07 }) => (
  <svg
    style={{ position: "absolute", right: 0, top: 0, width: 420, height: 420, pointerEvents: "none", opacity }}
    viewBox="0 0 420 420" fill="none" xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M420 0 L300 0 L300 80 L200 80 L200 160 L120 160 L120 260 L60 260 L60 420"
      stroke="#8cff2e" strokeWidth="1" strokeDasharray="6 4">
      <animate attributeName="stroke-dashoffset" from="0" to="-200" dur="8s" repeatCount="indefinite"/>
    </path>
    <path d="M420 100 L340 100 L340 200 L240 200 L240 300 L160 300 L160 420"
      stroke="#8cff2e" strokeWidth="0.6" strokeDasharray="4 6" opacity="0.6">
      <animate attributeName="stroke-dashoffset" from="0" to="200" dur="10s" repeatCount="indefinite"/>
    </path>
    <path d="M420 220 L380 220 L380 320 L280 320 L280 420"
      stroke="#8cff2e" strokeWidth="0.8" strokeDasharray="5 5" opacity="0.4">
      <animate attributeName="stroke-dashoffset" from="0" to="-150" dur="6s" repeatCount="indefinite"/>
    </path>
    {/* nodes */}
    {[[300,0],[200,80],[120,160],[60,260],[340,100],[240,200],[160,300],[380,220],[280,320]].map(([x,y],i) => (
      <circle key={i} cx={x} cy={y} r="3" fill="#8cff2e" opacity="0.5">
        <animate attributeName="opacity" values="0.2;0.8;0.2" dur={`${3 + i * 0.4}s`} repeatCount="indefinite"/>
      </circle>
    ))}
  </svg>
);
